// BioMax PaCC prototype root.
//
// Hash-router across the §7.x surfaces, derived entirely from the
// SURFACES registry (surfaces/registry.tsx). Nav is grouped by the
// surface taxonomy (Situational awareness / Forward analysis /
// Decision support / Governance). Shared scenario state lives in
// ScenarioContext so the pathogen×region×horizon selection follows
// the user between surfaces.

import { useEffect, useMemo, useState } from 'react';
import {
  listNonDisseminators, listBlockedAdapters,
} from '@biomax/plugin-surveillance-feed';
import {
  SURFACES, GROUP_ORDER, findSurface, DEFAULT_SURFACE_ID,
  SUB_GROUP_ORDER, SUB_GROUP_PERSONA,
  type SurfaceDef, type SurfaceSubGroup,
} from './surfaces/registry';
import { ScenarioProvider, useScenario, type NavLensPersona } from './ScenarioContext';
import { findPathogen } from '@biomax/plugin-pathogen-database';
import { findRegion } from '@biomax/plugin-region-database';
import { LIVENESS_STATUS, ATTENTION_COLOR, fmtSnapshotTs } from './status';
import { CommandPalette } from './CommandPalette';
import { DEPLOYMENT_MODE, MODE_META } from './mode';
import { listBriefPins, subscribeToBriefPins } from './pack/pin-to-brief';
import { HelpButton } from './HelpButton';
import { auditCapture } from './audit/capture';

function readScene(): string {
  // Strip leading `#/` AND any query/hash trailer (e.g. `#/country-profile?iso3=AUS`
  // is still the `country-profile` surface — the query is read by that surface).
  const id = window.location.hash.replace(/^#\/?/, '').split(/[?&]/)[0] ?? '';
  return findSurface(id) ? id : DEFAULT_SURFACE_ID;
}

export function App() {
  const [scene, setScene] = useState<string>(readScene);
  const [paletteOpen, setPaletteOpen] = useState(false);
  // L2 (CD review): mobile/tablet drawer state. Below 960px the nav rail
  // collapses to a hamburger-toggled off-canvas overlay.
  const [navOpen, setNavOpen] = useState(false);
  // R7 polish (CD): top-bar overflow menu (Tenant / Build / Connection) +
  // feed-health hover-expanded breakdown. Both default-closed; backdrop
  // click closes the overflow menu.
  const [feedExpanded, setFeedExpanded] = useState(false);
  const [topOvfOpen, setTopOvfOpen] = useState(false);

  useEffect(() => {
    const onHash = () => { setScene(readScene()); setNavOpen(false); };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Cmd-K / Ctrl-K opens the command palette (M1).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen(o => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const active = findSurface(scene) ?? SURFACES[0]!;
  const Active = active.Component;

  // CD R11 #5 — sovereignty / wire-ready coverage gaps surfaced on the
  // feed-status pill hover so operators see them where they already look.
  // `sovereigntyLimited` = countries that do not publish official surveillance
  // data; `wireReady` = wired adapters shipping with a structural blockedReason
  // (partnership / credential / paid-tier required to flip on). These are
  // register data (always available), ORTHOGONAL to the liveness count the
  // pill headlines (CD tidy-up §E reconciled the headline numbers into one
  // vocabulary, read from LIVENESS_STATUS; these counts ride along, never
  // merged into the liveness ratio).
  const feedGaps = useMemo(() => ({
    sovereigntyLimited: listNonDisseminators().length,
    wireReady: listBlockedAdapters().length,
  }), []);

  return (
    <ScenarioProvider>
      <div className={`pacc ${navOpen ? 'pacc--nav-open' : ''}`}>
        <header className="pacc__top">
          <button
            type="button"
            className="pacc__nav-toggle"
            aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={navOpen}
            aria-controls="pacc-nav"
            onClick={() => setNavOpen(o => !o)}
          >
            <span aria-hidden>{navOpen ? '✕' : '☰'}</span>
          </button>
          <span className="pacc__brand">BioMax PaCC</span>
          <span className="pacc__tagline">Pandemic Central Command · engineering prototype</span>
          <div className="pacc__top-spacer" />

          {/* R7 polish (CD): priority order left→right after the spacer is
              ⌘K · feed health · clock · EDU · ⋯ overflow. The operational
              chip (feed health) stays always-visible; secondary chips
              (tenant / build / connection) collapse into the overflow menu;
              everything `white-space: nowrap` so nothing leaks. */}

          {/* ⌘K — compact: kbd + dimmed verb. */}
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            title="Jump to surface (⌘K / Ctrl-K)"
            className="pacc__top-pill pacc__top-pill--btn"
          >
            ⌘K <span className="pacc__top-pill__dim">jump</span>
          </button>

          {/* Feed status — the reconciled liveness pill (CD tidy-up §E). ONE
              vocabulary, ONE denominator (total feeds): the pill is the
              COLLAPSED ribbon — hover for the same live / stale / staged
              breakdown that sums to the total. The dot encodes ATTENTION
              (is any supposed-to-be-live feed DOWN?), not the raw ratio, so
              expected-periodic stale feeds no longer paint a false emergency.
              Always present (reads the committed liveness snapshot), so the
              pill and ribbon can never diverge again. */}
          <FeedStatusPill
            expanded={feedExpanded}
            onExpand={setFeedExpanded}
            sovereigntyLimited={feedGaps.sovereigntyLimited}
            wireReady={feedGaps.wireReady}
          />

          {/* Clock — a LIVE ticking UTC clock (CD tidy-up §F). A frozen
              hardcoded "15:42 UTC" implied a data freshness that wasn't real
              (honesty-rail violation), so it's now ticking from the browser. */}
          <UtcClock />

          {/* Deployment mode indicator (CD R9 Q10 verdict: "dynamic mode
              indicator — not deleted — provenance"). Resolves at module-load
              from VITE_PACC_MODE; defaults to 'edu' for the prototype.
              Modes: sample / edu / staging / prod-tenant — each with its own
              colour + icon + tooltip per src/mode.ts MODE_META. */}
          <span
            role="note"
            aria-label={`Deployment mode: ${MODE_META[DEPLOYMENT_MODE].word}`}
            title={MODE_META[DEPLOYMENT_MODE].detail}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 9px',
              fontFamily: 'var(--bm-font-mono)', fontSize: 11, fontWeight: 700,
              letterSpacing: '.4px',
              color: MODE_META[DEPLOYMENT_MODE].colorVar,
              background: MODE_META[DEPLOYMENT_MODE].bgVar,
              border: `1px solid ${MODE_META[DEPLOYMENT_MODE].borderVar}`,
              borderRadius: 5,
              whiteSpace: 'nowrap' as const,
            }}
          >
            <span aria-hidden>{MODE_META[DEPLOYMENT_MODE].icon}</span>
            {MODE_META[DEPLOYMENT_MODE].word}
          </span>

          {/* ⋯ overflow menu for secondary chips (tenant / build / connection).
              Click toggles; backdrop click closes. */}
          <div className="pacc__top-ovf">
            <button
              type="button"
              className="pacc__top-pill pacc__top-pill--btn"
              onClick={() => setTopOvfOpen(o => !o)}
              aria-label="More chips"
              aria-expanded={topOvfOpen}
            >⋯</button>
            {topOvfOpen && (
              <>
                <button
                  type="button"
                  className="pacc__top-ovf__backdrop"
                  aria-label="Close menu"
                  onClick={() => setTopOvfOpen(false)}
                />
                <div className="pacc__top-ovf__menu" role="menu">
                  <div className="pacc__top-ovf__item">Tenant · WHO/CDC pilot</div>
                  <div className="pacc__top-ovf__item">Build · v0.1</div>
                  <div className="pacc__top-ovf__item">Connection · live</div>
                </div>
              </>
            )}
          </div>
        </header>

        {/* App-wide INVESTIGATIONAL coverage ribbon (deploy-pipeline #3).
            Persistent + unmissable on EVERY surface — not just Feed Health —
            so a deployed user always sees what this is + how live the data is
            right now. Pulls the honest "N of M live" + last-ingest timestamp
            from the bundled liveness snapshot (LIVENESS), produced by the
            6-hourly ingest+deploy. Never fabricates an "all live" headline. */}
        <CoverageRibbon />

        {/* Backdrop covers the main pane below 960px when the nav drawer is
            open — tap to close. Hidden by CSS above 960px. */}
        <button
          type="button"
          className="pacc__nav-backdrop"
          aria-label="Close navigation"
          onClick={() => setNavOpen(false)}
          tabIndex={navOpen ? 0 : -1}
        />
        <SideNav scene={scene} />

        <main className="pacc__main">
          <SessionContextBar surfaceId={active.id} surfaceLabel={active.label} surfaceSection={active.section} surfaceGroup={active.group} />
          <Active />
        </main>

        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} activeId={scene} />

        <footer className="pacc__status">
          {/* CD tidy-up §E — the footer counts are the CATALOG scope ("what we
              track"), a different noun from the top-bar/ribbon FEED liveness.
              Labelled explicitly so the operator never reads "15 pathogens" as
              a third, contradictory live-count. */}
          <span className="pacc__status-dot" />
          <span style={{ color: 'var(--bm-text-subtle)', textTransform: 'uppercase', letterSpacing: '.5px', fontSize: 10 }}>Tracking</span>
          Engine v0.1 · catalog db480134… · 35 regions · 15 pathogens · {SURFACES.length} surfaces
          <span style={{ color: 'var(--bm-text-subtle)' }}>— catalog scope, not feed liveness</span>
          <span className="pacc__status-spacer" />
          {/* EDU disclaimer moved to the top bar (CD L1); keep the footer
              showing build/data provenance only. */}
        </footer>
      </div>
    </ScenarioProvider>
  );
}

/**
 * App-wide coverage ribbon (deploy-pipeline #3).
 *
 * A persistent, unmissable band under the top bar on every surface. It states
 * what PaCC is (INVESTIGATIONAL · not for operational use) and the honest live
 * coverage drawn from the bundled liveness snapshot the 6-hourly ingest+deploy
 * produces: "<live> / <reportable> sources live · last ingest <timestamp>".
 *
 * `reportable = total − staged` so the denominator excludes not-yet-live-by-
 * design (gate-blocked / wire-ready) adapters — counting them would understate
 * coverage dishonestly in the other direction. Staged is shown separately.
 *
 * When no scheduled ingest has run yet (LIVENESS is null — generatedAt empty /
 * total 0), we say so plainly rather than invent a count. We never seed an "all
 * live" headline. Click navigates to the §7.22 Feed Health surface for detail.
 */
function CoverageRibbon() {
  // CD tidy-up §E — the ribbon is the EXPANDED form of the top-bar pill: it
  // reads the SAME reconciled liveness status (one denominator = total feeds),
  // so live + stale + staged sum to the total by construction. The accent
  // colour follows the same ATTENTION axis the pill's dot uses (down → degraded,
  // some-stale → stale, all-flowing → live). No more "30/56" reportable
  // denominator that didn't reconcile with the pill's "30/79".
  const S = LIVENESS_STATUS;
  const ts = fmtSnapshotTs(S.generatedAt);
  const accent = ATTENTION_COLOR[S.attention];
  const segPct = (n: number): string => (S.total > 0 ? `${(n / S.total) * 100}%` : '0%');

  return (
    <div
      role="note"
      aria-label={
        S.ready
          ? `Investigational prototype — not for operational use. Of ${S.total} feeds: ${S.live} live, ${S.stale} stale, ${S.staged} staged. Last ingest ${ts}.`
          : 'Investigational prototype — not for operational use. Live coverage pending the first scheduled ingest.'
      }
      onClick={() => { window.location.hash = '#/feed-health'; }}
      title="Live surveillance coverage — click to open Feed Health §7.22"
      className="pacc__ribbon"
      style={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12,
        padding: '6px 16px',
        background: 'var(--bm-bg-panel, var(--bm-bg))',
        borderBottom: '1px solid var(--bm-border)',
        borderTop: `2px solid ${accent}`,
        fontFamily: 'var(--bm-font-mono)', fontSize: 11.5,
        cursor: 'pointer', whiteSpace: 'nowrap' as const,
      }}
    >
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontWeight: 700, letterSpacing: '.5px',
        color: 'var(--bm-state-stale, #fbbf24)',
      }}>
        <span aria-hidden>⚠</span> INVESTIGATIONAL
      </span>
      <span style={{ color: 'var(--bm-text-subtle)' }}>·</span>
      <span style={{ color: 'var(--bm-text-muted)' }}>not for operational use</span>
      <span style={{ color: 'var(--bm-text-subtle)' }}>·</span>
      {S.ready ? (
        <>
          <span style={{
            color: 'var(--bm-text-subtle)', textTransform: 'uppercase',
            letterSpacing: '.6px', fontSize: 9.5,
          }}>Live coverage</span>
          {/* The three honest segments — same vocabulary as the pill popover. */}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--bm-text)' }}>
            <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--bm-state-live)', display: 'inline-block' }} />
            <b style={{ color: 'var(--bm-state-live)' }}>{S.live} live</b>
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--bm-text)' }}>
            <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--bm-state-stale)', display: 'inline-block' }} />
            {S.stale} stale
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--bm-text)' }}>
            <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--bm-text-subtle)', display: 'inline-block' }} />
            {S.staged} staged
          </span>
          {/* The sum is shown explicitly so the operator can SEE it reconciles. */}
          <span style={{ color: 'var(--bm-text-muted)' }}>= {S.total} feeds</span>
          {/* Proportion bar — colour is never the only signal (count + label
              carry it too); CVD-safe state tokens. */}
          <span aria-hidden style={{
            display: 'inline-flex', width: 120, height: 7, borderRadius: 4,
            overflow: 'hidden', border: '1px solid var(--bm-border)',
          }}>
            <span style={{ display: 'block', height: '100%', width: segPct(S.live), background: 'var(--bm-state-live)' }} />
            <span style={{ display: 'block', height: '100%', width: segPct(S.stale), background: 'var(--bm-state-stale)' }} />
            <span style={{ display: 'block', height: '100%', width: segPct(S.staged), background: 'var(--bm-text-subtle)' }} />
          </span>
          <span style={{ color: 'var(--bm-text-subtle)' }}>·</span>
          <span style={{ color: 'var(--bm-text-muted)' }}>last ingest {ts}</span>
        </>
      ) : (
        <span style={{ color: 'var(--bm-text-muted)' }}>
          live coverage — pending first scheduled ingest
        </span>
      )}
      <span style={{ flex: 1 }} />
      <span style={{ color: 'var(--bm-text-subtle)', fontSize: 10.5 }}>
        Feed Health §7.22 →
      </span>
    </div>
  );
}

/**
 * CD tidy-up §F — a LIVE UTC clock. The previous top bar showed a hardcoded
 * `15:42 UTC` — a frozen fake time in a "live command centre" implies a data
 * freshness that isn't real, which violates the honesty rule. Per the CD
 * verdict (tidyup-layout.jsx ClockVerdict) we make it tick rather than remove
 * it. Updates once a minute (HH:MM granularity — a seconds tick is visual
 * noise in chrome). aria-hidden of the colon keeps screen-reader output clean;
 * the visible label IS the time so it's not colour-only.
 */
function UtcClock() {
  const fmt = () => {
    const d = new Date();
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const mm = String(d.getUTCMinutes()).padStart(2, '0');
    return `${hh}:${mm} UTC`;
  };
  const [now, setNow] = useState(fmt);
  useEffect(() => {
    const tick = () => setNow(fmt());
    tick();
    const id = setInterval(tick, 30_000); // sub-minute so the minute never lags
    return () => clearInterval(id);
  }, []);
  return (
    <span
      className="pacc__top-pill pacc__top-pill--mono"
      role="note"
      aria-label={`Current time ${now}`}
      title="Live UTC clock (your browser's clock)"
    >
      {now}
    </span>
  );
}

/**
 * CD tidy-up §E — the reconciled feed-status pill. The collapsed form of the
 * INVESTIGATIONAL ribbon: same numbers, same ONE vocabulary. Reads the
 * committed liveness snapshot (always present), so it can never show a
 * different denominator from the ribbon. Hover/focus expands the same
 * live / stale / staged breakdown; the dot encodes attention (any feed DOWN),
 * not the raw ratio. Sovereignty / wire-ready counts (orthogonal coverage
 * gaps, CD R11 #5) ride along as a clickable deep-link when present.
 */
function FeedStatusPill({
  expanded, onExpand, sovereigntyLimited, wireReady,
}: {
  expanded: boolean;
  onExpand: (v: boolean) => void;
  sovereigntyLimited: number;
  wireReady: number;
}) {
  const S = LIVENESS_STATUS;
  const stateAttr = S.attention === 'down' ? 'conflict' : S.attention === 'stale' ? 'stale' : 'confirmed';
  const ts = fmtSnapshotTs(S.generatedAt);

  if (!S.ready) {
    return (
      <span
        className="pacc__feed-pill"
        data-state="stale"
        role="note"
        aria-label="Feed status pending the first scheduled ingest"
        title="No scheduled ingest has run yet"
        style={{ cursor: 'default' }}
      >
        <span className="pacc__feed-pill__dot" aria-hidden />
        <span className="pacc__feed-pill__count">feeds — pending</span>
      </span>
    );
  }

  const aria =
    `Feed status: of ${S.total} feeds, ${S.live} live, ${S.stale} stale`
    + `${S.staged ? `, ${S.staged} staged` : ''}`
    + `${S.down ? `, ${S.down} down` : ''}`
    + `${sovereigntyLimited ? `, ${sovereigntyLimited} sovereignty-limited` : ''}`
    + `${wireReady ? `, ${wireReady} wire-ready` : ''}`;

  return (
    <button
      type="button"
      className="pacc__feed-pill"
      onMouseEnter={() => onExpand(true)}
      onMouseLeave={() => onExpand(false)}
      onFocus={() => onExpand(true)}
      onBlur={() => onExpand(false)}
      onClick={() => { window.location.hash = '#/feed-health'; }}
      aria-label={aria}
      data-state={stateAttr}
    >
      <span className="pacc__feed-pill__dot" aria-hidden />
      {/* The headline: live / total — same denominator as the ribbon. */}
      <span className="pacc__feed-pill__count">
        {S.live} live <span className="pacc__feed-pill__den">/ {S.total} feeds</span>
      </span>
      {expanded && (
        <span className="pacc__feed-pill__detail">
          {/* The pill IS the collapsed ribbon — same three segments, summing
              to the total, so hovering the pill reconciles against the ribbon. */}
          <span className="pacc__feed-pill__row">
            <span className="pacc__feed-pill__k" style={{ color: 'var(--bm-state-live)' }}>● live</span>
            <span>{S.live}</span><span className="pacc__feed-pill__hint">on cadence</span>
          </span>
          <span className="pacc__feed-pill__row">
            <span className="pacc__feed-pill__k" style={{ color: 'var(--bm-state-stale)' }}>● stale</span>
            <span>{S.stale}</span>
            <span className="pacc__feed-pill__hint">{S.down ? `incl. ${S.down} down` : 'past cadence — incl. periodic reference'}</span>
          </span>
          <span className="pacc__feed-pill__row">
            <span className="pacc__feed-pill__k" style={{ color: 'var(--bm-text-subtle)' }}>● staged</span>
            <span>{S.staged}</span><span className="pacc__feed-pill__hint">wired, not yet ingesting</span>
          </span>
          <span className="pacc__feed-pill__row">
            <span className="pacc__feed-pill__k" style={{ color: 'var(--bm-text-muted)' }}>= total</span>
            <span>{S.total}</span><span className="pacc__feed-pill__hint">feeds</span>
          </span>
          {(sovereigntyLimited > 0 || wireReady > 0) && (
            <>
              <span className="pacc__feed-pill__rule" aria-hidden />
              {/* CD R11 #5: sovereignty / wire-ready deep-link — orthogonal
                  coverage gaps, never folded into the liveness count. */}
              <span
                role="link"
                tabIndex={0}
                className="pacc__feed-pill__sovereignty"
                title="Open Feed Health · Sovereignty tab"
                onClick={(e) => { e.stopPropagation(); window.location.hash = '#/feed-health?tab=sovereignty'; }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault(); e.stopPropagation();
                    window.location.hash = '#/feed-health?tab=sovereignty';
                  }
                }}
              >
                {sovereigntyLimited} sovereignty-limited · {wireReady} wire-ready
              </span>
            </>
          )}
          {ts && <span className="pacc__feed-pill__sub">as of {ts} · click for Feed Health §7.22</span>}
        </span>
      )}
    </button>
  );
}

/** Phase 8.4 — Session Context Bar (CD R9 Q7 verdict: "a persistent
 *  session context bar beats a tab-document model for this product — an
 *  operator investigates one thing deeply per shift, not many in parallel.
 *  The bar carries the subject + breadcrumb across Inbox → Map → Compare
 *  → Brief so context never resets — and feeds the briefing automatically").
 *
 *  v1 (this commit) is the minimum-viable form: shows the current surface
 *  + section + group as the breadcrumb. Real "Investigating X" subject
 *  state lands in Phase 9 with the cross-surface session-state
 *  refactor (currently each surface owns its own state). */
function SessionContextBar({
  surfaceId, surfaceLabel, surfaceSection, surfaceGroup,
}: {
  surfaceId: string;
  surfaceLabel: string;
  surfaceSection: string;
  surfaceGroup: string;
}) {
  // Live pinned-count subscription (CD R9 long-term spec: "📌 N pinned" in
  // the session bar — clickable to jump to §7.25 Minister Pack)
  const [pinCount, setPinCount] = useState(() => listBriefPins().length);
  useEffect(() => subscribeToBriefPins(pins => setPinCount(pins.length)), []);

  // CD R11 #10 — replace the stale "subject-tracking lands Phase 11"
  // placeholder (Phase 11 has shipped) with the live subject: the
  // pathogen × region the operator is investigating, drawn from the
  // shared ScenarioContext. This is the minimal-real form of the
  // subject-tracking the R9 session bar was designed for; the full
  // cross-surface SubjectContext (per-surface override) is R11 #11 / V1.5.
  const { pathogenId, regionId } = useScenario();
  const subjectPathogen = findPathogen(pathogenId)?.names.common ?? pathogenId;
  const subjectRegion = findRegion(regionId)?.names.english ?? regionId;

  return (
    <div
      role="navigation"
      aria-label="Session context"
      style={{
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        margin: '0 0 var(--bm-space-3) 0',
        padding: '8px 14px',
        background: 'var(--bm-bg-panel, var(--bm-bg))',
        border: '1px solid var(--bm-border)',
        borderRadius: 9,
        fontFamily: 'var(--bm-font-mono)', fontSize: 11,
      }}
    >
      <span style={{
        fontSize: 9.5, fontWeight: 700, letterSpacing: '.8px', textTransform: 'uppercase',
        color: 'var(--bm-text-subtle)',
      }}>Session</span>
      <span style={{ color: 'var(--bm-text-muted)' }}>{surfaceGroup}</span>
      <span style={{ color: 'var(--bm-text-subtle)' }}>›</span>
      <span style={{ fontWeight: 700, color: 'var(--bm-text)', fontSize: 12 }}>
        {surfaceLabel}
      </span>
      <span style={{
        color: 'var(--bm-text-subtle)',
        fontFamily: 'var(--bm-font-mono)', fontSize: 10,
      }}>{surfaceSection}</span>
      <HelpButton surfaceId={surfaceId} />
      <span style={{ flex: 1 }} />
      {pinCount > 0 && (
        <a
          href="#/minister-briefing"
          title="Click to open the Minister Briefing Pack with your pinned items"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '2px 9px',
            fontFamily: 'var(--bm-font-mono)', fontSize: 11, fontWeight: 700,
            color: 'var(--bm-accent, #6ea3ff)',
            background: 'var(--bm-accent-subtle, rgba(110,163,255,0.14))',
            border: '1px solid var(--bm-accent, #6ea3ff)',
            borderRadius: 5, textDecoration: 'none' as const,
          }}
        >
          📌 {pinCount} pinned for brief
        </a>
      )}
      <span
        title="Investigating subject — shared pathogen × region selection (ScenarioContext). Per-surface overrides land in V1.5."
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '2px 9px',
          fontFamily: 'var(--bm-font-mono)', fontSize: 10.5,
          color: 'var(--bm-text-muted)',
          background: 'var(--bm-bg-subtle)',
          border: '1px solid var(--bm-border)',
          borderRadius: 5,
        }}
      >
        <span style={{
          fontSize: 9, fontWeight: 700, letterSpacing: '.6px',
          textTransform: 'uppercase', color: 'var(--bm-text-subtle)',
        }}>Investigating</span>
        <span style={{ color: 'var(--bm-text)', fontWeight: 600 }}>{subjectPathogen}</span>
        <span style={{ color: 'var(--bm-text-subtle)' }}>·</span>
        <span style={{ color: 'var(--bm-text)' }}>{subjectRegion}</span>
      </span>
    </div>
  );
}

/**
 * CD R11 #2 — Decision-support is sub-grouped + nav-side persona lens.
 *
 * Most groups (Situational awareness / Forward analysis / Governance) render
 * flat as before. Decision support renders each sub-group (Respond / Deploy
 * / Capacity / Economics / Overlays) with a small label header and any
 * surfaces missing a subGroup tagged onto a default "Other" cluster (none
 * today; safety rail for future entries).
 *
 * The persona lens (above the nav, compact toggle) is `none` by default;
 * choosing "Finance" sets `navLensPersona = finance-minister` which causes
 * the Economics sub-group to LIGHT (▲ Finance badge + brighter chip
 * background + accent border). Other clusters render unchanged — clustering,
 * not filtering, preserves discoverability (CD R11 mockup verdict).
 *
 * Pulled out of App so it can use `useScenario()` (which lives inside the
 * ScenarioProvider mounted by App's JSX tree).
 */
function SideNav({ scene }: { scene: string }) {
  const { navLensPersona, setNavLensPersona } = useScenario();

  // CD tidy-up §B — collapsible groups so the ~36-surface rail stops being a
  // 36-row scroll-with-a-visible-track. Default: only the group holding the
  // ACTIVE surface is open (others collapsed to a single labelled, counted
  // header row); when the active surface moves to another group, that group
  // opens. A user can still open any group; their explicit choices are kept
  // in `open` and merged with the active-group rule.
  const activeGroup = useMemo(
    () => findSurface(scene)?.group ?? GROUP_ORDER[0]!,
    [scene],
  );
  // null = "follow the active group"; a record = explicit user overrides.
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const isOpen = (group: string): boolean =>
    group in open ? open[group]! : group === activeGroup;
  const toggle = (group: string) =>
    setOpen(o => ({ ...o, [group]: !(group in o ? o[group]! : group === activeGroup) }));

  const handlePersonaChange = (next: NavLensPersona) => {
    if (next === navLensPersona) return;
    auditCapture.emit({
      event: {
        type: 'persona.switch',
        surface: 'nav-lens',
        subjectIds: [],
        payload: { scope: 'nav-cluster-lens', from: navLensPersona, to: next },
      },
    });
    setNavLensPersona(next);
  };

  return (
    <nav id="pacc-nav" className="pacc__nav" aria-label="Command centre">
      <NavLensToggle persona={navLensPersona} onChange={handlePersonaChange} />
      {GROUP_ORDER.map(group => {
        const groupSurfaces = SURFACES.filter(s => s.group === group);
        const expanded = isOpen(group);
        const holdsActive = groupSurfaces.some(s => s.id === scene);
        return (
          <div key={group} className="pacc__nav-group">
            <button
              type="button"
              className="pacc__nav-section pacc__nav-section--btn"
              aria-expanded={expanded}
              onClick={() => toggle(group)}
            >
              <span className={`pacc__nav-caret ${expanded ? 'is-open' : ''}`} aria-hidden>▸</span>
              <span className="pacc__nav-section-label">{group}</span>
              {/* Count badge per group (CD §B). */}
              <span className="pacc__nav-section-count">{groupSurfaces.length}</span>
              {/* Active-dot when this collapsed group holds the active surface,
                  so the operator can still see where they are without expanding. */}
              {!expanded && holdsActive && (
                <span className="pacc__nav-section-activedot" title="active surface in this group" aria-hidden />
              )}
            </button>
            {expanded && (group === 'Decision support'
              ? renderSubGrouped(groupSurfaces, scene, navLensPersona)
              : groupSurfaces.map(s => <NavItem key={s.id} surface={s} active={s.id === scene} />)
            )}
          </div>
        );
      })}
    </nav>
  );
}

/** Render a Decision-support group as ordered sub-group clusters. Sub-groups
 *  matching `SUB_GROUP_PERSONA[g] === persona` get the "lit" styling. */
function renderSubGrouped(surfaces: readonly SurfaceDef[], scene: string, persona: NavLensPersona) {
  return SUB_GROUP_ORDER.map(sub => {
    const items = surfaces.filter(s => s.subGroup === sub);
    if (items.length === 0) return null;
    const lit = persona !== 'none' && SUB_GROUP_PERSONA[sub] === persona;
    return (
      <SubGroupCluster key={sub} sub={sub} items={items} scene={scene} lit={lit} persona={persona} />
    );
  });
}

function SubGroupCluster({
  sub, items, scene, lit, persona,
}: {
  sub: SurfaceSubGroup;
  items: readonly SurfaceDef[];
  scene: string;
  lit: boolean;
  persona: NavLensPersona;
}) {
  const litPersonaLabel = lit && persona !== 'none' ? personaShortLabel(persona) : null;
  return (
    <div
      style={{
        margin: '0 0 6px 0',
        padding: lit ? '4px 4px 4px 4px' : 0,
        background: lit ? 'var(--bm-accent-subtle, rgba(110,163,255,0.10))' : 'transparent',
        border: lit ? '1px solid var(--bm-accent, #6ea3ff)' : '1px solid transparent',
        borderRadius: lit ? 6 : 0,
      }}
    >
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          margin: '4px 4px 4px 8px',
          fontFamily: 'var(--bm-font-mono)', fontSize: 9.5, fontWeight: 700,
          letterSpacing: '.8px', textTransform: 'uppercase',
          color: lit ? 'var(--bm-accent, #6ea3ff)' : 'var(--bm-text-subtle)',
        }}
      >
        <span>{sub}</span>
        {litPersonaLabel && (
          <span
            title={`Sub-group lit by ${litPersonaLabel} persona lens`}
            style={{
              padding: '1px 6px',
              fontSize: 9, fontWeight: 700,
              color: 'var(--bm-bg, #fff)',
              background: 'var(--bm-accent, #6ea3ff)',
              borderRadius: 4,
              letterSpacing: '.4px',
            }}
          >▲ {litPersonaLabel}</span>
        )}
      </div>
      {items.map(s => <NavItem key={s.id} surface={s} active={s.id === scene} />)}
    </div>
  );
}

function NavItem({ surface, active }: { surface: SurfaceDef; active: boolean }) {
  // R7 polish (CD): the canonical short label fits one line at the
  // 232px column; the `title` is the safety net for any future
  // long label OR for keyword-rich aliases (the first keyword is
  // typically a longer descriptive form — e.g. 'Per-pathogen' →
  // 'per-pathogen drilldown'). Search palette keeps the full
  // keywords list so jump-to-surface stays open.
  const longForm = surface.keywords?.find(k => k.length > surface.label.length && k.toLowerCase().includes(surface.label.toLowerCase()));
  const fullName = longForm ? `${surface.label} · ${longForm}` : surface.label;
  return (
    <a
      href={`#/${surface.id}`}
      className={`pacc__nav-item ${active ? 'is-active' : ''}`}
      title={fullName}
    >
      {/* Label-first (R5 §-demote) + canonical short labels
          (R7 polish). The § becomes quiet right-aligned mono
          metadata for anyone who speaks in section numbers. */}
      <span className="pacc__nav-item-label">{surface.label}</span>
      <span className="pacc__nav-item-sec">{surface.section}</span>
    </a>
  );
}

function NavLensToggle({ persona, onChange }: { persona: NavLensPersona; onChange: (p: NavLensPersona) => void }) {
  // Compact persona-lens chooser pinned above the nav. Currently exposes
  // None + Finance (Economics-cluster lit). More personas can land without
  // a structural change — add another entry to OPTIONS + a
  // `SUB_GROUP_PERSONA[<group>]: <persona>` mapping in registry.tsx.
  const OPTIONS: ReadonlyArray<{ k: NavLensPersona; label: string; title: string }> = [
    { k: 'none',             label: 'None',    title: 'No persona lens active — all sub-groups render unstyled' },
    { k: 'finance-minister', label: 'Finance', title: 'Finance Minister lens — lights the Economics cluster (Cost-per-DALY + Insurance Triggers)' },
  ];
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
        margin: '0 0 10px 0',
        padding: '6px 8px',
        background: 'var(--bm-bg-subtle, transparent)',
        border: '1px solid var(--bm-border)',
        borderRadius: 5,
      }}
    >
      <span
        style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 9.5, fontWeight: 700,
          letterSpacing: '.6px', textTransform: 'uppercase',
          color: 'var(--bm-text-muted)',
          marginRight: 4,
        }}
      >Persona lens</span>
      <div style={{ display: 'inline-flex', border: '1px solid var(--bm-border)', borderRadius: 4 }}>
        {OPTIONS.map(opt => {
          const on = persona === opt.k;
          return (
            <button
              key={opt.k}
              type="button"
              onClick={() => onChange(opt.k)}
              title={opt.title}
              aria-pressed={on}
              style={{
                padding: '3px 9px',
                fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 600,
                color: on ? 'var(--bm-text)' : 'var(--bm-text-subtle)',
                background: on ? 'var(--bm-bg)' : 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >{opt.label}</button>
          );
        })}
      </div>
    </div>
  );
}

function personaShortLabel(p: Exclude<NavLensPersona, 'none'>): string {
  // Single-source short-label mapping for the nav-side persona lens. Keeps
  // the lit-cluster badge text in sync with the toggle label.
  if (p === 'finance-minister') return 'Finance';
  // Exhaustiveness rail — if a new NavLensPersona member lands without a
  // short label, TS will surface it via the `never` collapse here.
  const _exhaustive: never = p;
  return _exhaustive;
}
